import type { TableCell } from '@worldathletics/types';

export type GridCell = {
  r: number;
  c: number;
  cell: TableCell;
  isAnchor: boolean;
};

function inferColumnCount(rows: TableCell[][]): number {
  let max = 0;
  for (const row of rows) {
    let width = 0;
    for (const cell of row) {
      width += cell.colSpan ?? 1;
    }
    if (width > max) {
      max = width;
    }
  }
  return max;
}

export function expandTableGrid(
  rows: TableCell[][],
  colCount?: number,
): GridCell[][] {
  const totalCols = colCount ?? inferColumnCount(rows);
  const occupied: (GridCell | null)[][] = rows.map(() =>
    Array<GridCell | null>(totalCols).fill(null),
  );

  for (let r = 0; r < rows.length; r += 1) {
    const row = rows[r]!;
    let c = 0;
    for (const cell of row) {
      while (c < totalCols && occupied[r]![c] !== null) {
        c += 1;
      }
      if (c >= totalCols) {
        throw new Error(
          `expandTableGrid: overlap detected in column range at row ${String(r)}`,
        );
      }
      const rowSpan = cell.rowSpan ?? 1;
      const colSpan = cell.colSpan ?? 1;
      if (c + colSpan > totalCols) {
        throw new Error(
          `expandTableGrid: cell at (${String(r)},${String(c)}) colSpan ${String(colSpan)} exceeds ${String(totalCols)}-column grid`,
        );
      }
      if (r + rowSpan > rows.length) {
        throw new Error(
          `expandTableGrid: cell at (${String(r)},${String(c)}) rowSpan ${String(rowSpan)} exceeds ${String(rows.length)} rows`,
        );
      }
      const anchor: GridCell = { r, c, cell, isAnchor: true };
      for (let rr = 0; rr < rowSpan; rr += 1) {
        for (let cc = 0; cc < colSpan; cc += 1) {
          const tr = r + rr;
          const tc = c + cc;
          if (occupied[tr]![tc] !== null) {
            throw new Error(
              `expandTableGrid: span overlap at (${String(tr)},${String(tc)})`,
            );
          }
          occupied[tr]![tc] =
            rr === 0 && cc === 0
              ? anchor
              : { r: tr, c: tc, cell, isAnchor: false };
        }
      }
      c += colSpan;
    }
  }

  // Pad any unfilled positions with empty anchor cells. Real-world PDF
  // imports sometimes emit ragged rows (a header row with merged cells
  // whose colSpans the parser failed to capture, or a shorter row in a
  // table whose inferred width came from a wider sibling). Throwing here
  // would crash the entire document view; padding lets the table render
  // with the missing positions shown as empty cells.
  return occupied.map((row, r) =>
    row.map((gc, c) => gc ?? { r, c, cell: { text: '' }, isAnchor: true }),
  );
}
